import React from 'react';
import { Table, Tabs, Tab } from 'react-bootstrap';

const DiscrepanciesComparison = ({ 
  model1Name, 
  model2Name, 
  model3Name, 
  headerDiscrepancies, 
  lineItemDiscrepancies,
  resolutions,
  onResolutionChange,
  hasStoredDiscrepancyMap = false
}) => {
  /**
   * Normalize value to handle empty strings, null, undefined, and N/A consistently.
   * @deprecated Discrepancy detection should be read from the backend response's
   * discrepancyMap (when hasStoredDiscrepancyMap is true) instead of using local
   * normalization. Retained for backward compatibility with legacy jobs that have
   * no stored discrepancy map.
   */
  const normalizeModelValue = (val) => {
    if (val === null || val === undefined) return null;
    const strVal = String(val).trim();
    if (strVal === '' || strVal === 'N/A') return null;
    return strVal;
  };

  /**
   * Check if there's a discrepancy using consensus-based approach.
   * @deprecated Discrepancy detection should be read from the backend response's
   * discrepancyMap (when hasStoredDiscrepancyMap is true) instead of using this
   * local comparison. Retained for backward compatibility with legacy jobs that
   * have no stored discrepancy map.
   */
  const hasDiscrepancy = (model1, model2, model3) => {
    const val1 = normalizeModelValue(model1);
    const val2 = normalizeModelValue(model2);
    const val3 = normalizeModelValue(model3);
    
    // Count how many models agree on each value (including null)
    const valueCounts = {};
    [val1, val2, val3].forEach(val => {
      const key = val === null ? '__NULL__' : val;
      valueCounts[key] = (valueCounts[key] || 0) + 1;
    });
    
    // If any value (including null) has 2 or more models agreeing, no discrepancy
    const hasConsensus = Object.values(valueCounts).some(count => count >= 2);
    
    // Discrepancy only if all three values are different (no consensus)
    return !hasConsensus;
  };

  // Get the maximum number of line items from any model
  const getMaxLineItems = () => {
    if (!lineItemDiscrepancies) return 0;
    
    const maxLineItems = Math.max(
      lineItemDiscrepancies.model1?.length || 0,
      lineItemDiscrepancies.model2?.length || 0,
      lineItemDiscrepancies.model3?.length || 0,
      lineItemDiscrepancies.final?.length || 0
    );
    
    return maxLineItems;
  };
  
  // Create an array of indices for the line items
  const lineIndices = Array.from({ length: getMaxLineItems() }, (_, i) => i);

  // Extract all unique keys from all line items across all models
  const getAllKeys = (lineIndex) => {
    if (!lineItemDiscrepancies) return [];
    
    const keySets = [];
    
    // Get keys from model1 line items
    if (lineItemDiscrepancies.model1 && Array.isArray(lineItemDiscrepancies.model1) && 
        lineItemDiscrepancies.model1[lineIndex]) {
      keySets.push(Object.keys(lineItemDiscrepancies.model1[lineIndex]));
    }
    
    // Get keys from model2 line items
    if (lineItemDiscrepancies.model2 && Array.isArray(lineItemDiscrepancies.model2) && 
        lineItemDiscrepancies.model2[lineIndex]) {
      keySets.push(Object.keys(lineItemDiscrepancies.model2[lineIndex]));
    }
    
    // Get keys from model3 line items if available
    if (lineItemDiscrepancies.model3 && Array.isArray(lineItemDiscrepancies.model3) && 
        lineItemDiscrepancies.model3[lineIndex]) {
      keySets.push(Object.keys(lineItemDiscrepancies.model3[lineIndex]));
    }
    
    // Get keys from final line items if available
    if (lineItemDiscrepancies.final && Array.isArray(lineItemDiscrepancies.final) && 
        lineItemDiscrepancies.final[lineIndex]) {
      keySets.push(Object.keys(lineItemDiscrepancies.final[lineIndex]));
    }
    
    // Flatten and get unique keys
    return [...new Set(keySets.flat())];
  };
  
  // Check if there are discrepancies in line items
  const hasLineItemDiscrepancies = () => {
    if (!lineItemDiscrepancies) return false;
    
    // When a stored discrepancy map is available, the backend already provides
    // pre-filtered discrepancy data — all line item fields present are discrepancies
    if (hasStoredDiscrepancyMap) {
      return getMaxLineItems() > 0;
    }
    
    // Legacy fallback: check each line item using local consensus logic
    for (let i = 0; i < getMaxLineItems(); i++) {
      const keys = getAllKeys(i);
      
      // Check each field in the line item
      for (const key of keys) {
        const model1Value = lineItemDiscrepancies.model1?.[i]?.[key];
        const model2Value = lineItemDiscrepancies.model2?.[i]?.[key];
        const model3Value = lineItemDiscrepancies.model3?.[i]?.[key];
        
        // Use consensus-based discrepancy check
        if (hasDiscrepancy(model1Value, model2Value, model3Value)) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Determine which tab should be active by default
  const getDefaultActiveKey = () => {
    if (Object.keys(headerDiscrepancies || {}).length > 0) {
      return "header";
    } else if (hasLineItemDiscrepancies()) {
      return "lineItems";
    }
    return "header";
  };

  return (
    <div className="mt-4">
      <h6>Discrepancies to Resolve</h6>
      
      <Tabs defaultActiveKey={getDefaultActiveKey()} id="discrepancies-tabs">
        {Object.keys(headerDiscrepancies || {}).length > 0 && (
          <Tab eventKey="header" title="Header Discrepancies">
            <Table striped bordered hover className="mt-3">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>{model1Name} Value</th>
                  <th>{model2Name} Value</th>
                  {model3Name && <th>{model3Name} Value</th>}
                  <th>Recommended</th>
                  <th>Resolution</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(headerDiscrepancies || {}).map(([field, values]) => (
                  <tr key={field}>
                    <td>{field}</td>
                    <td>{values.model1Value !== null && values.model1Value !== undefined ? values.model1Value.toString() : 'N/A'}</td>
                    <td>{values.model2Value !== null && values.model2Value !== undefined ? values.model2Value.toString() : 'N/A'}</td>
                    {model3Name && <td>{values.tiebreakerValue !== null && values.tiebreakerValue !== undefined ? values.tiebreakerValue.toString() : 'N/A'}</td>}
                    <td>{values.recommended !== null && values.recommended !== undefined ? values.recommended.toString() : 'N/A'}</td>
                    <td>
                      <select 
                        className="form-select"
                        value={resolutions[field] || ''}
                        onChange={(e) => onResolutionChange(field, e.target.value)}
                      >
                        <option value={values.model1Value}>{model1Name} Value</option>
                        <option value={values.model2Value}>{model2Name} Value</option>
                        {values.tiebreakerValue !== undefined && (
                          <option value={values.tiebreakerValue}>{model3Name} Value</option>
                        )}
                        <option value="">None (Skip)</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab>
        )}
        
        {lineItemDiscrepancies && getMaxLineItems() > 0 && hasLineItemDiscrepancies() && (
          <Tab eventKey="lineItems" title="Line Item Discrepancies">
            <Tabs defaultActiveKey={0} id="line-items-discrepancies-tabs" className="mt-3">
              {lineIndices.map(index => {
                // Get all keys for this line item
                const keys = getAllKeys(index);
                
                // Check if this line item has any discrepancies
                // When stored map is available, all provided line item data represents discrepancies
                const hasDiscrepancies = hasStoredDiscrepancyMap
                  ? keys.length > 0
                  : keys.some(key => {
                      const model1Value = lineItemDiscrepancies.model1?.[index]?.[key];
                      const model2Value = lineItemDiscrepancies.model2?.[index]?.[key];
                      const model3Value = lineItemDiscrepancies.model3?.[index]?.[key];
                      
                      // Use consensus-based discrepancy check (legacy fallback)
                      return hasDiscrepancy(model1Value, model2Value, model3Value);
                    });
                
                // Only show line items with discrepancies
                if (!hasDiscrepancies) return null;
                
                return (
                  <Tab key={index} eventKey={index} title={`Line Item ${index + 1}`}>
                    <Table striped bordered hover className="mt-3">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>{model1Name} Value</th>
                          <th>{model2Name} Value</th>
                          {model3Name && <th>{model3Name} Value</th>}
                          <th>Final Value</th>
                          <th>Resolution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map(key => {
                          const fieldId = `LineItems[${index}].${key}`;
                          const model1Value = lineItemDiscrepancies.model1?.[index]?.[key];
                          const model2Value = lineItemDiscrepancies.model2?.[index]?.[key];
                          const model3Value = lineItemDiscrepancies.model3?.[index]?.[key];
                          const finalValue = lineItemDiscrepancies.final?.[index]?.[key];
                          
                          // When stored map is available, all provided fields are discrepancies;
                          // otherwise use local consensus-based check (legacy fallback)
                          const hasFieldDiscrepancy = hasStoredDiscrepancyMap
                            ? true
                            : hasDiscrepancy(model1Value, model2Value, model3Value);
                          
                          // Only show fields with discrepancies
                          if (!hasFieldDiscrepancy) return null;
                          
                          return (
                            <tr key={key}>
                              <td>{key}</td>
                              <td>{model1Value !== null && model1Value !== undefined ? model1Value.toString() : 'N/A'}</td>
                              <td>{model2Value !== null && model2Value !== undefined ? model2Value.toString() : 'N/A'}</td>
                              {model3Name && <td>{model3Value !== null && model3Value !== undefined ? model3Value.toString() : 'N/A'}</td>}
                              <td>{finalValue !== null && finalValue !== undefined ? finalValue.toString() : 'N/A'}</td>
                              <td>
                                <select 
                                  className="form-select"
                                  value={resolutions[fieldId] || ''}
                                  onChange={(e) => onResolutionChange(fieldId, e.target.value)}
                                >
                                  <option value={model1Value !== undefined ? model1Value : ''}>{model1Name} Value</option>
                                  <option value={model2Value !== undefined ? model2Value : ''}>{model2Name} Value</option>
                                  {model3Name && model3Value !== undefined && (
                                    <option value={model3Value}>{model3Name} Value</option>
                                  )}
                                  <option value="">None (Skip)</option>
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Tab>
                );
              })}
            </Tabs>
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default DiscrepanciesComparison;